import { useState, useRef, useCallback } from 'react';

/**
 * Hook for managing game animation states.
 * Handles trick wins, joker bonuses, finish animations, and card movements.
 */
export function useGameAnimations() {
  // Animation states
  const [trickWinAnimation, setTrickWinAnimation] = useState(null);
  const [finishAnimation, setFinishAnimation] = useState(null);
  const [frozenLastPlay, setFrozenLastPlay] = useState(null);

  // Card animation state
  const [cardAnimations, setCardAnimations] = useState([]);
  const [playingCards, setPlayingCards] = useState([]);
  const [drawnCard, setDrawnCard] = useState(null);
  const [flyingCards, setFlyingCards] = useState([]); // Unified flying card animations
  const [fadingSeals, setFadingSeals] = useState([]); // Card keys with HAND seals fading out

  // Refs for animation targets
  const lastPlayRef = useRef(null);
  const drawPileRef = useRef(null);
  const handContainerRef = useRef(null);

  // Show trick win animation
  const showTrickWin = useCallback(({ playerName, points, isMe, jokerEffects }) => {
    const jokerBonus = jokerEffects?.reduce((sum, e) => sum + e.change, 0) || 0;
    const basePoints = points - jokerBonus;

    setTrickWinAnimation({
      playerName: isMe ? 'Du' : playerName,
      points: basePoints,
      isMe
    });

    // Clear animation
    setTimeout(() => {
      setTrickWinAnimation(null);
      setFrozenLastPlay(null);
    }, 1800);
  }, []);

  // Show finish animation
  const showFinish = useCallback(({ playerName, position, points, isMe, totalPlayers }) => {
    const isLastPlace = position === totalPlayers;
    const positionEmojis = ['🥇', '🥈', '🥉'];

    setFinishAnimation({
      playerName: isMe ? 'Du' : playerName,
      position,
      positionEmoji: isLastPlace ? '💩' : (positionEmojis[position - 1] || `#${position}`),
      points,
      isMe,
      isLastPlace
    });

    const duration = 7000; // 7 Sekunden für alle Finish-Splashes
    setTimeout(() => setFinishAnimation(null), duration);
  }, []);

  // Add card animation badge
  const addCardAnimation = useCallback(({ type, cards, reason, icon, text }) => {
    const animId = Date.now() + Math.random();
    setCardAnimations([{
      id: animId,
      type,
      cards,
      reason,
      icon,
      text
    }]);

    setTimeout(() => {
      setCardAnimations(prev => prev.filter(a => a.id !== animId));
    }, 2000);
  }, []);

  // Show drawn card animation
  const showDrawnCard = useCallback((card) => {
    const drawPileRect = drawPileRef.current?.getBoundingClientRect();
    const handRect = handContainerRef.current?.getBoundingClientRect();

    if (drawPileRect && handRect) {
      const startX = drawPileRect.left + drawPileRect.width / 2;
      const startY = drawPileRect.top + drawPileRect.height / 2;
      const endX = handRect.left + handRect.width / 2;
      const endY = handRect.top + handRect.height / 2;

      setDrawnCard({
        card,
        startX,
        startY,
        offsetX: endX - startX,
        offsetY: endY - startY
      });

      setTimeout(() => setDrawnCard(null), 1050);
    }
  }, []);

  // Set playing cards with animation offsets
  const setPlayingCardsWithAnimation = useCallback((cards, targetRef) => {
    const targetRect = targetRef?.current?.getBoundingClientRect() || lastPlayRef.current?.getBoundingClientRect();
    const targetX = targetRect ? targetRect.left + targetRect.width / 2 : window.innerWidth / 2;
    const targetY = targetRect ? targetRect.top + targetRect.height / 2 : window.innerHeight / 2;

    const cardsWithOffsets = cards.map(card => {
      const cardKey = `${card.rank}-${card.suit}`;
      const cardElement = document.querySelector(`[data-card-key="${cardKey}"]`);
      if (cardElement) {
        const cardRect = cardElement.getBoundingClientRect();
        const cardCenterX = cardRect.left + cardRect.width / 2;
        const cardCenterY = cardRect.top + cardRect.height / 2;
        return {
          ...card,
          offsetX: targetX - cardCenterX,
          offsetY: targetY - cardCenterY
        };
      }
      return { ...card, offsetX: 0, offsetY: -250 };
    });

    setPlayingCards(cardsWithOffsets);
  }, []);

  const clearPlayingCards = useCallback(() => {
    setPlayingCards([]);
  }, []);

  // Add flying cards (for draw/discard animations - to MY hand)
  const addFlyingCards = useCallback((cards, type, options = {}) => {
    const drawPileRect = drawPileRef.current?.getBoundingClientRect();
    const handRect = handContainerRef.current?.getBoundingClientRect();

    if (!handRect) return;

    const handCenterX = handRect.left + handRect.width / 2;
    const handCenterY = handRect.top + handRect.height / 2;
    const deckCenterX = drawPileRect ? drawPileRect.left + drawPileRect.width / 2 : handCenterX;
    const deckCenterY = drawPileRect ? drawPileRect.top + drawPileRect.height / 2 : handCenterY - 200;

    const newFlyingCards = cards.map((card, idx) => {
      const id = `${Date.now()}-${idx}-${Math.random()}`;

      if (type === 'added') {
        // Deck → Hand
        return {
          id,
          card,
          type: 'added',
          startX: deckCenterX,
          startY: deckCenterY,
          endX: handCenterX,
          endY: handCenterY,
          delay: idx * 100,
          seal: options.seal || null  // Pass seal for badge display
        };
      } else if (type === 'toDeck') {
        // Hand → Deck (for swap)
        return {
          id,
          card,
          type: 'toDeck',
          startX: handCenterX,
          startY: handCenterY,
          endX: deckCenterX,
          endY: deckCenterY,
          delay: idx * 100
        };
      } else {
        // Hand → Down (discard)
        return {
          id,
          card,
          type: 'removed',
          startX: handCenterX,
          startY: handCenterY,
          endX: handCenterX,
          endY: handCenterY + 150, // Fly down
          delay: idx * 100
        };
      }
    });

    setFlyingCards(prev => [...prev, ...newFlyingCards]);

    // Clear after animation completes
    const duration = type === 'added' ? 900 : 800;
    const totalDelay = cards.length * 100 + duration;
    setTimeout(() => {
      setFlyingCards(prev => prev.filter(fc => !newFlyingCards.find(nfc => nfc.id === fc.id)));
    }, totalDelay);
  }, []);

  // Add flying cards for OTHER players (deck → their PlayerCard, face-down)
  const addOtherPlayerFlyingCards = useCallback((cardCount, targetPlayerIndex) => {
    const drawPileRect = drawPileRef.current?.getBoundingClientRect();

    // Find the target player's card - try multiple elements (header rows)
    const allPlayerCards = document.querySelectorAll(`[data-player-index="${targetPlayerIndex}"]`);

    // Find the first VISIBLE element (has dimensions)
    let targetRect = null;
    for (const el of allPlayerCards) {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        targetRect = rect;
        break;
      }
    }

    if (!targetRect) return; // Can't find visible target, skip animation

    const deckCenterX = drawPileRect ? drawPileRect.left + drawPileRect.width / 2 : window.innerWidth / 2;
    const deckCenterY = drawPileRect ? drawPileRect.top + drawPileRect.height / 2 : 100;
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const targetCenterY = targetRect.top + targetRect.height / 2;

    const newFlyingCards = [];
    for (let i = 0; i < cardCount; i++) {
      const id = `other-${Date.now()}-${i}-${Math.random()}`;
      newFlyingCards.push({
        id,
        card: { faceDown: true }, // Face-down card
        type: 'addedToOther',
        startX: deckCenterX,
        startY: deckCenterY,
        endX: targetCenterX,
        endY: targetCenterY,
        delay: i * 100
      });
    }

    setFlyingCards(prev => [...prev, ...newFlyingCards]);

    // Clear after animation completes
    const duration = 700;
    const totalDelay = cardCount * 100 + duration;
    setTimeout(() => {
      setFlyingCards(prev => prev.filter(fc => !newFlyingCards.find(nfc => nfc.id === fc.id)));
    }, totalDelay);
  }, []);

  // Add flying cards for OTHER players' card plays (PlayerCard → lastPlay)
  const addOtherPlayerPlayingCards = useCallback((cards, sourcePlayerIndex) => {
    // Find the source player's card - try multiple selectors
    // On mobile: only the header row may be visible
    const allPlayerCards = document.querySelectorAll(`[data-player-index="${sourcePlayerIndex}"]`);

    // Find the first VISIBLE element (has dimensions)
    let sourceRect = null;
    for (const el of allPlayerCards) {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        sourceRect = rect;
        break;
      }
    }

    // Find the lastPlay container
    const lastPlayRect = lastPlayRef.current?.getBoundingClientRect();

    // Skip animation if we can't find visible source element
    if (!sourceRect || !lastPlayRect) return;

    const sourceCenterX = sourceRect.left + sourceRect.width / 2;
    const sourceCenterY = sourceRect.top + sourceRect.height / 2;
    const targetCenterX = lastPlayRect.left + lastPlayRect.width / 2;
    const targetCenterY = lastPlayRect.top + lastPlayRect.height / 2;

    const newFlyingCards = cards.map((card, idx) => {
      const id = `play-${Date.now()}-${idx}-${Math.random()}`;
      return {
        id,
        card,
        type: 'playedByOther',
        startX: sourceCenterX,
        startY: sourceCenterY,
        endX: targetCenterX,
        endY: targetCenterY,
        delay: idx * 50 // Faster stagger for plays
      };
    });

    setFlyingCards(prev => [...prev, ...newFlyingCards]);

    // Clear after animation completes
    const duration = 500;
    const totalDelay = cards.length * 50 + duration;
    setTimeout(() => {
      setFlyingCards(prev => prev.filter(fc => !newFlyingCards.find(nfc => nfc.id === fc.id)));
    }, totalDelay);
  }, []);

  return {
    // Animation states
    trickWinAnimation,
    finishAnimation,
    frozenLastPlay,
    cardAnimations,
    playingCards,
    drawnCard,
    flyingCards,
    fadingSeals,

    // Refs
    lastPlayRef,
    drawPileRef,
    handContainerRef,

    // Setters (for direct state updates from socket handlers)
    setTrickWinAnimation,
    setFinishAnimation,
    setFrozenLastPlay,
    setCardAnimations,
    setPlayingCards,
    setDrawnCard,
    setFlyingCards,
    setFadingSeals,

    // Animation helpers
    showTrickWin,
    showFinish,
    addCardAnimation,
    showDrawnCard,
    addFlyingCards,
    addOtherPlayerFlyingCards,
    addOtherPlayerPlayingCards,
    setPlayingCardsWithAnimation,
    clearPlayingCards,
  };
}
